import React, { useEffect, useRef, useState } from "react";
import axios from "../axiosConfig";
import {
  articleTitleFromSearchParams,
  fetchWikiSection,
  setArticleSearchParam,
  wikiArticleShareUrl,
} from "../wikiSectionApi";
import { sanitizeQaWikiAnswer } from "../sanitizeQaAnswer";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

/** Safe text inside inline HTML for react-markdown + rehypeRaw citation chips. */
function escapeHtmlText(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function ReasoningStatus() {
  return (
    <p
      className="mb-3 flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400"
      role="status"
      aria-live="polite"
    >
      <span className="qa-hourglass text-slate-400 dark:text-slate-500" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 3h12" />
          <path d="M6 21h12" />
          <path d="M8 4c0 3.2 4 4.2 4 8s-4 4.8-4 8" />
          <path d="M16 4c0 3.2-4 4.2-4 8s4 4.8 4 8" />
        </svg>
      </span>
      Reasoning
    </p>
  );
}

function wrapCitationForDisplay(label) {
  const t = String(label).replace(/\s+/g, " ").trim();
  if (!t) return "";
  const inner = escapeHtmlText(`[${t}]`);
  return `<span class="qa-inline-citation not-prose mx-0.5 inline-block rounded border border-slate-200/90 bg-slate-50 px-1 py-0.5 text-[11px] font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-200">${inner}</span>`;
}

/**
 * Q&A — ask against wiki + optional Cloudflare; wiki sources open an inline
 * reader (full article); document sources download. No section browser here.
 */
/** Radware product lines for manual document-family filtering in QA. */
const RADWARE_PRODUCT_FAMILIES = [
  "Alteon",
  "API Security",
  "Agentic AI",
  "DefensePro",
  "Cloud DDoS",
  "Cloud WAF",
  "K8S WAF",
  "CyberController",
  "General Topics",
];

/** Source labels add " (pN)" / " (pN-pM)" (and possible float page ids); strip for save-as name. */
function filenameForDocumentDownload(displayTitle) {
  const s = String(displayTitle || "").trim();
  if (!s) return "document";
  return s.replace(/\s+\(p[^)]+\)\s*$/i, "").trim() || "document";
}

function isHttpSourceUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url.trim());
}

function isWikiArticleSourceUrl(url) {
  return typeof url === "string" && url.startsWith("wiki:");
}

function wikiTitleFromSourceUrl(url) {
  if (!isWikiArticleSourceUrl(url)) return String(url || "");
  return url.slice(5);
}

function isWikiDocumentSourceUrl(url) {
  return (
    typeof url === "string" &&
    (url.startsWith("wiki_documents:") || url.startsWith("wiki_documents_competitive:"))
  );
}

/** Indices to show in the Sources list: cited only (max 5), or primary when none cited. */
const MAX_VISIBLE_SOURCES = 5;

function visibleSourceIndices(result) {
  const titles = result?.document_titles;
  if (!Array.isArray(titles) || titles.length === 0) return [];

  const cited = result?.source_cited;
  let indices;
  if (Array.isArray(cited) && cited.some(Boolean)) {
    indices = titles.map((_, i) => i).filter((i) => cited[i] === true);
  } else {
    indices = [0];
  }
  return indices.slice(0, MAX_VISIBLE_SOURCES);
}

/** Server-built cited sources (preferred); fallback for older API responses. */
function sourcesForDisplayList(result) {
  const built = result?.display_sources;
  if (Array.isArray(built) && built.length > 0) {
    return built.slice(0, MAX_VISIBLE_SOURCES);
  }
  return visibleSourceIndices(result).map((j) => ({
    title: result.document_titles[j],
    url: result.source_urls?.[j] ?? "",
    kind: result.source_kinds?.[j] ?? "wiki",
  }));
}

/** Prefer real filename from download response (no page suffix). */
function filenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const utf8 = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      return utf8[1].trim().replace(/^["']|["']$/g, "");
    }
  }
  const ascii = /filename\s*=\s*("?)([^";\n]+)\1/i.exec(header);
  if (ascii) return ascii[2].trim().replace(/^["']|["']$/g, "");
  return null;
}

function applySimpleTableClasses(html) {
  if (!html) return html;

  return html
    .replace(
      /<table\b(?![^>]*class=)/gi,
      '<table class="w-full table-auto border border-slate-200 text-sm leading-6 dark:border-slate-600"'
    )
    .replace(
      /<table\b([^>]*)class="([^"]*)"/gi,
      '<table $1class="$2 w-full table-auto border border-slate-200 text-sm leading-6 dark:border-slate-600"'
    )
    .replace(/<thead\b(?![^>]*class=)/gi, '<thead class="bg-slate-50 dark:bg-slate-800/80"')
    .replace(
      /<th\b(?![^>]*class=)/gi,
      '<th class="px-3 py-2 text-left font-semibold text-slate-800 border border-slate-200 align-top dark:border-slate-600 dark:text-slate-100"'
    )
    .replace(
      /<td\b(?![^>]*class=)/gi,
      '<td class="px-3 py-2 border border-slate-200 align-top whitespace-pre-wrap break-words dark:border-slate-600"'
    );
}

const articleMarkdownComponents = {
  a: ({ node, children, ...props }) => (
    <a
      {...props}
      className="break-words text-blue-600 underline hover:text-blue-800 dark:text-blue-400"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: (props) => (
    <table
      className="my-4 w-full border-collapse border border-slate-300 dark:border-slate-600"
      {...props}
    />
  ),
  th: (props) => (
    <th
      className="border border-slate-300 bg-slate-100 px-3 py-2 text-left text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
      {...props}
    />
  ),
  td: (props) => (
    <td className="border border-slate-300 px-3 py-2 dark:border-slate-600" {...props} />
  ),
};

function WikiArticleBody({ blocks }) {
  const seen = new Set();

  return blocks
    .filter((blk) => blk.type === "wiki")
    .map((blk, i) => {
      const raw = blk.text || "";
      if (!raw || raw.trim().length < 5) return null;

      const key = "txt:" + raw.replace(/\s+/g, " ").trim();
      if (seen.has(key)) return null;
      seen.add(key);

      const hasHtmlTable = /<table[\s\S]*?>[\s\S]*?<\/table>/.test(raw);

      if (hasHtmlTable) {
        const parts = raw.split(/(<table[\s\S]*?<\/table>)/gi);

        return (
          <div key={i} className="mb-6 space-y-4">
            {parts.map((part, idx) => {
              if (!part || !part.trim()) return null;

              const looksLikeTable = /^<table[\s\S]*<\/table>$/i.test(part.trim());

              if (looksLikeTable) {
                const pretty = applySimpleTableClasses(part);
                return (
                  <div key={`tbl-${idx}`} className="not-prose my-4 overflow-x-auto">
                    <div dangerouslySetInnerHTML={{ __html: pretty }} />
                  </div>
                );
              }

              return (
                <ReactMarkdown
                  key={`txt-${idx}`}
                  className="prose prose-slate max-w-full break-words dark:prose-invert"
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={articleMarkdownComponents}
                >
                  {part}
                </ReactMarkdown>
              );
            })}
          </div>
        );
      }

      return (
        <div key={i} className="mb-6">
          <ReactMarkdown
            className="prose prose-slate max-w-full break-words dark:prose-invert"
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={articleMarkdownComponents}
          >
            {raw}
          </ReactMarkdown>
        </div>
      );
    });
}

function WikiArticlePanel({ blocks, loading, error, onClose }) {
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);
  const first = blocks?.[0];
  const articleTitle =
    typeof first?.title === "string" && first.title.trim() ? first.title.trim() : "";
  const canOpenInEditor =
    !loading && !error && blocks?.length > 0 && articleTitle.length > 0;

  const copyShareLink = async () => {
    const url = wikiArticleShareUrl("/qa", articleTitle);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy article link:", err);
    }
  };

  return (
    <aside
      className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-600 dark:bg-slate-900/90"
    >
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-600">
        <h2 className="text-base font-semibold text-slate-900 dark:text-white">
          Wiki article
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {canOpenInEditor && (
            <>
              <button
                type="button"
                onClick={copyShareLink}
                className="rounded border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                {linkCopied ? "Copied!" : "Copy link"}
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/editor?article=${encodeURIComponent(articleTitle)}`
                  )
                }
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
              >
                Open in Editor
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {loading && (
          <p className="text-sm text-slate-600 dark:text-slate-400">Loading article…</p>
        )}
        {error && !loading && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        {!loading && !error && blocks?.length > 0 && (
          <div className="prose prose-slate max-w-none dark:prose-invert">
            {first?.mainSection && (
              <p className="not-prose mb-2 text-sm text-slate-500 dark:text-slate-400">
                {first.mainSection}
                {first.subSection ? ` → ${first.subSection}` : ""}
              </p>
            )}
            <h3 className="not-prose mb-3 text-xl font-bold text-slate-900 dark:text-white">
              {first?.title || "Untitled"}
            </h3>
            {(first?.createdAt || first?.updatedAt) && (
              <p className="not-prose mb-4 text-sm italic text-slate-500 dark:text-slate-400">
                {first?.createdAt && (
                  <>Created {new Date(first.createdAt).toLocaleDateString()}</>
                )}
                {first?.updatedAt && first.updatedAt !== first.createdAt && (
                  <>
                    {" "}
                    · Updated {new Date(first.updatedAt).toLocaleDateString()}
                  </>
                )}
              </p>
            )}
            <WikiArticleBody blocks={blocks} />
          </div>
        )}
      </div>
    </aside>
  );
}

export default function WikiQA() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const articleAsideRef = useRef(null);
  const loadedArticleParamRef = useRef(null);
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [documentsFilterMode, setDocumentsFilterMode] = useState("auto");
  const [documentsFilterType, setDocumentsFilterType] = useState("single"); // "single" | "multi"
  const [documentsFamilySingle, setDocumentsFamilySingle] = useState("");
  const [documentsFamiliesMulti, setDocumentsFamiliesMulti] = useState([]);
  const [documentsFilterStrict, setDocumentsFilterStrict] = useState(true);
  const [sessionId] = useState(() => `wikiqa-${Date.now()}`);
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedArticleBlocks, setSelectedArticleBlocks] = useState(null);
  const [articleViewerLoading, setArticleViewerLoading] = useState(false);
  const [articleViewerError, setArticleViewerError] = useState(null);
  const [askError, setAskError] = useState(null);

  const API_BASE = "/api";
  const articleParam = articleTitleFromSearchParams(searchParams);

  const showArticlePanel =
    articleViewerLoading || selectedArticleBlocks !== null || articleViewerError;

  useEffect(() => {
    if (selectedArticleBlocks?.length && articleAsideRef.current) {
      articleAsideRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedArticleBlocks]);

  const closeArticleViewer = () => {
    loadedArticleParamRef.current = null;
    setSelectedArticleBlocks(null);
    setArticleViewerError(null);
    setArticleViewerLoading(false);
    setArticleSearchParam(setSearchParams, null);
  };

  const sanitizeContent = (text, ragId) => sanitizeQaWikiAnswer(text, ragId);

  const formatAnswerCitationsForDisplay = (text, result) => {
    if (!text) return text;
    const titles = Array.isArray(result?.document_titles) ? result.document_titles : [];
    if (!titles.length) return text;

    let out = String(text);

    // Replace legacy [SOURCE n] markers with a styled source chip (rehypeRaw).
    out = out.replace(/\[\s*SOURCE\s*(\d+)\s*\]/gi, (_, nStr) => {
      const n = Number(nStr);
      if (!Number.isInteger(n) || n < 1 || n > titles.length) return "";
      return wrapCitationForDisplay(titles[n - 1]);
    });

    // Replace plain numeric citations [n] (but not markdown links like [n](...)).
    out = out.replace(/\[(\d+)\](?!\()/g, (m, nStr) => {
      const n = Number(nStr);
      if (!Number.isInteger(n) || n < 1 || n > titles.length) return m;
      return wrapCitationForDisplay(titles[n - 1]);
    });

    out = out
      .replace(/\[\s*\]/g, "")
      // Preserve line breaks so markdown tables/code blocks keep working.
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return out;
  };

  const loadWikiArticle = async (title) => {
    const clean = String(title || "").trim();
    if (!clean) return;

    setArticleViewerLoading(true);
    setArticleViewerError(null);
    setSelectedArticleBlocks(null);

    try {
      const res = await fetchWikiSection(clean);
      const data = res.data;
      const blocks = Array.isArray(data) ? data : [data];
      if (blocks.length) {
        setSelectedArticleBlocks(blocks);
      } else {
        setArticleViewerError("No content was returned for this article.");
      }
    } catch (err) {
      console.error("Failed to load the article:", err);
      setArticleViewerError("Could not load this article. Try again later.");
    } finally {
      setArticleViewerLoading(false);
    }
  };

  // Open article from shareable ?article= links (and keep URL in sync when param changes).
  useEffect(() => {
    if (!articleParam) {
      if (loadedArticleParamRef.current) {
        loadedArticleParamRef.current = null;
        setSelectedArticleBlocks(null);
        setArticleViewerError(null);
        setArticleViewerLoading(false);
      }
      return;
    }
    if (loadedArticleParamRef.current === articleParam) return;
    loadedArticleParamRef.current = articleParam;
    loadWikiArticle(articleParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleParam]);

  const openDisplaySource = ({ title, url, kind }) => {
    const href = String(url || "").trim();
    const k = String(kind || "").toLowerCase();

    if (k === "web" || isHttpSourceUrl(href)) {
      if (href) window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (k === "pdf" || isWikiDocumentSourceUrl(href)) {
      downloadDocumentSource(href, title);
      return;
    }
    if (k === "wiki" || isWikiArticleSourceUrl(href)) {
      const articleTitle = isWikiArticleSourceUrl(href)
        ? wikiTitleFromSourceUrl(href)
        : title;
      const clean = String(articleTitle || "").trim();
      if (!clean) return;
      loadedArticleParamRef.current = clean;
      setArticleSearchParam(setSearchParams, clean);
      loadWikiArticle(clean);
    }
  };

  const handleAsk = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setAskError(null);

    try {
      const selectedManualFamilies =
        documentsFilterType === "single"
          ? documentsFamilySingle
            ? [documentsFamilySingle]
            : []
          : documentsFamiliesMulti;
      const payload = {
        question,
        sessionId,
        documents_library_vendor: "radware",
        documents_filter_mode: documentsFilterMode,
        documents_product_families: selectedManualFamilies,
        documents_filter_strict: documentsFilterStrict,
        history: chatHistory.slice(-3).map((entry) => ({
          question: entry.question,
          answer:
            entry.results?.map((r) => `${r.rag_id}: ${r.Answer}`).join("\n\n") ||
            "",
        })),
      };
      let res;
      try {
        res = await axios.post(`${API_BASE}/multi-answer`, payload);
      } catch (err) {
        // The proxy sometimes closes the first connection. Retry that once.
        if (err?.response?.status === 401) throw err;
        res = await axios.post(`${API_BASE}/multi-answer`, payload);
      }

      const newEntry = {
        question,
        results: res.data.results || [],
      };

      setChatHistory((prev) => [...prev, newEntry]);
      setQuestion("");
    } catch (err) {
      console.error("Error fetching multi-answer:", err);
      const status = err?.response?.status;
      setAskError(
        status === 401
          ? "Your session expired. Please log in again."
          : "The answer request failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const hasConversation = chatHistory.length > 0;
  const splitView = showArticlePanel && hasConversation;

  const downloadDocumentSource = async (sourceId, fallbackName = "document") => {
    try {
      const parts = String(sourceId || "").split(":");
      const collection = parts[0];
      const canDownloadFromCollection =
        collection === "wiki_documents" || collection === "wiki_documents_competitive";
      if (parts.length < 3 || !canDownloadFromCollection) {
        throw new Error("Not a downloadable wiki documents source");
      }
      const documentId = parts[1];
      const res = await axios.get(`${API_BASE}/documents/${documentId}/download`, {
        responseType: "blob",
      });
      const cd =
        res.headers?.["content-disposition"] ?? res.headers?.["Content-Disposition"];
      const fromServer = filenameFromContentDisposition(cd);
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fromServer || filenameForDocumentDownload(fallbackName);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download referenced document source:", err);
    }
  };

  return (
    <div className="relative w-full">
      {/* Full-width ask / controls */}
      <section className="w-full">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 max-w-2xl">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
              Ask &amp; search
            </h1>
            <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              Answers draw from wiki articles, uploaded documents, and competitive info.
              Name a competitor explicitly to search that database.{" "}
              <button
                type="button"
                onClick={() => navigate("/activity")}
                className="text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-slate-700 dark:text-slate-400 dark:decoration-slate-600 dark:hover:text-slate-200"
              >
                Usage stats
              </button>
            </p>
          </div>
          {hasConversation && (
            <button
              type="button"
              className="min-h-[40px] shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:bg-transparent dark:text-slate-200 dark:hover:bg-slate-800"
              onClick={() => {
                setChatHistory([]);
                setQuestion("");
                closeArticleViewer();
              }}
            >
              Start new conversation
            </button>
          )}
        </div>

        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <input
            type="text"
            placeholder="Ask a question…"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAsk();
              }
            }}
            className="w-full min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
          <button
            type="button"
            onClick={handleAsk}
            className="min-h-[44px] shrink-0 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-500"
            disabled={isLoading}
          >
            Ask
          </button>
        </div>
        {isLoading ? <ReasoningStatus /> : null}
        {askError ? (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">{askError}</p>
        ) : null}

        <div className="mb-6 border-b border-slate-200 pb-4 dark:border-slate-700">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Documents filter
            </span>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="documents-filter-mode"
                checked={documentsFilterMode === "auto"}
                onChange={() => setDocumentsFilterMode("auto")}
                className="accent-blue-600"
              />
              Auto (classifier)
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="documents-filter-mode"
                checked={documentsFilterMode === "manual"}
                onChange={() => setDocumentsFilterMode("manual")}
                className="accent-blue-600"
              />
              Manual (product families)
            </label>
          </div>

          {documentsFilterMode === "manual" && (
            <div className="mt-3 space-y-3 border-t border-slate-100 pt-3 dark:border-slate-800">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="radio"
                    name="documents-filter-type"
                    checked={documentsFilterType === "single"}
                    onChange={() => setDocumentsFilterType("single")}
                    className="accent-blue-600"
                  />
                  Single family
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="radio"
                    name="documents-filter-type"
                    checked={documentsFilterType === "multi"}
                    onChange={() => setDocumentsFilterType("multi")}
                    className="accent-blue-600"
                  />
                  Multi family
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={documentsFilterStrict}
                    onChange={(e) => setDocumentsFilterStrict(e.target.checked)}
                    className="accent-blue-600"
                  />
                  Strict (no cross-family fallback)
                </label>
              </div>

              {documentsFilterType === "single" ? (
                <select
                  value={documentsFamilySingle}
                  onChange={(e) => setDocumentsFamilySingle(e.target.value)}
                  className="min-h-[40px] w-full max-w-sm rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="">Select one product family</option>
                  {RADWARE_PRODUCT_FAMILIES.map((pf) => (
                    <option key={pf} value={pf}>
                      {pf}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {RADWARE_PRODUCT_FAMILIES.map((pf) => {
                    const selected = documentsFamiliesMulti.includes(pf);
                    return (
                      <button
                        key={pf}
                        type="button"
                        onClick={() =>
                          setDocumentsFamiliesMulti((prev) =>
                            selected ? prev.filter((x) => x !== pf) : [...prev, pf]
                          )
                        }
                        className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                          selected
                            ? "border-blue-600 bg-blue-600 text-white"
                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        }`}
                      >
                        {pf}
                      </button>
                    );
                  })}
                </div>
              )}

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Wiki/manual articles are always considered. Manual families apply only to Radware uploaded documents.
              </p>
            </div>
          )}
        </div>

        {!hasConversation && !showArticlePanel && !isLoading && (
          <div className="mb-2 flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-slate-200 px-6 py-12 dark:border-slate-700">
            <p className="max-w-md text-center text-sm text-slate-500 dark:text-slate-400">
              No messages yet. Ask a question to see answers and sources here.
            </p>
          </div>
        )}
      </section>

      {/* Below ask: conversation + optional article (grid on large screens) */}
      {(hasConversation || showArticlePanel) && (
        <section
          className={
            splitView
              ? "grid w-full max-w-[1600px] grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.14fr)] lg:items-stretch lg:gap-8"
              : "w-full max-w-[1600px]"
          }
        >
          {hasConversation && (
            <div
              className={
                "flex min-h-0 min-w-0 flex-col " +
                (splitView
                  ? "max-h-[min(70vh,calc(100vh-13rem))] lg:max-h-[calc(100vh-13rem)]"
                  : "max-h-[min(70vh,calc(100vh-13rem))]")
              }
            >
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <div className="space-y-8">
                  <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                    Conversation
                  </h2>

                  {[...chatHistory].reverse().map((entry, i) => (
                    <div
                      key={i}
                      className="border-b border-slate-200 pb-8 last:border-b-0 last:pb-0 dark:border-slate-700"
                    >
                      <p className="mb-4 text-sm text-slate-900 dark:text-slate-100 sm:text-base">
                        <span className="font-semibold text-slate-500 dark:text-slate-400">
                          You
                        </span>
                        <span className="mx-2 text-slate-300 dark:text-slate-600">·</span>
                        {entry.question}
                      </p>

                      <div
                        className={
                          splitView || (entry.results || []).length <= 1
                            ? "grid grid-cols-1 gap-4"
                            : "grid grid-cols-1 gap-4 md:grid-cols-2"
                        }
                      >
                {(entry.results || []).map((result, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/60 sm:p-5"
                  >
                    <div className="mb-3">
                      <span className="inline-block rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                        {result.rag_id?.toUpperCase()}
                      </span>
                    </div>

                    {(() => {
                      const displayAnswer = formatAnswerCitationsForDisplay(
                        sanitizeContent(result.Answer || "", result.rag_id),
                        result
                      );
                      if (!displayAnswer) {
                        return (
                          <p className="text-sm italic text-slate-500 dark:text-slate-400">
                            No answer text was returned for this question.
                          </p>
                        );
                      }
                      return (
                    <ReactMarkdown
                      className="prose prose-slate max-w-prose text-sm leading-relaxed dark:prose-invert"
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        a: ({ node, children, ...props }) => (
                          <a
                            {...props}
                            className="break-words text-blue-600 underline hover:text-blue-800 dark:text-blue-400"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {children}
                          </a>
                        ),
                        table: ({ node, ...props }) => (
                          <table
                            className="my-4 w-full border-collapse border border-slate-300 dark:border-slate-600"
                            {...props}
                          />
                        ),
                        th: ({ node, ...props }) => (
                          <th
                            className="border border-slate-300 bg-slate-100 px-3 py-2 text-left text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                            {...props}
                          />
                        ),
                        td: ({ node, ...props }) => (
                          <td
                            className="border border-slate-300 px-3 py-2 dark:border-slate-600"
                            {...props}
                          />
                        ),
                      }}
                    >
                      {displayAnswer}
                    </ReactMarkdown>
                      );
                    })()}

                    {sourcesForDisplayList(result).length > 0 && (
                      <div className="mt-4 border-t border-slate-100 pt-3 text-sm dark:border-slate-800">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Sources
                        </span>
                        <ul className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                          {sourcesForDisplayList(result).map((src, j) => {
                            const title = src.title;
                            const url = src.url;
                            const kind = String(src.kind || "").toLowerCase();
                            const isWeb = kind === "web" || isHttpSourceUrl(url);
                            const isPdf =
                              kind === "pdf" || isWikiDocumentSourceUrl(url);
                            const isWikiManual =
                              kind === "wiki" || isWikiArticleSourceUrl(url);
                            const primaryBadge =
                              j === 0 ? (
                                <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                                  (primary)
                                </span>
                              ) : null;
                            const linkClass =
                              "py-1.5 text-left text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300";

                            if (result.rag_id === "wiki") {
                              if (isWeb && isHttpSourceUrl(url)) {
                                return (
                                  <li key={`${title}-${j}`}>
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`${linkClass} block underline decoration-blue-600/20 underline-offset-2`}
                                    >
                                      {title}
                                      {primaryBadge}
                                    </a>
                                  </li>
                                );
                              }

                              if (isWeb || isPdf || isWikiManual) {
                                return (
                                  <li key={`${title}-${j}`}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openDisplaySource({ title, url, kind })
                                      }
                                      className={`${linkClass} underline decoration-blue-600/20 underline-offset-2`}
                                    >
                                      {title}
                                      {primaryBadge}
                                    </button>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={`${title}-${j}`}
                                  className="py-1.5 text-sm text-slate-600 dark:text-slate-400"
                                >
                                  {title}
                                  {primaryBadge}
                                </li>
                              );
                            }

                            return (
                              <li key={`${title}-${j}`}>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`${linkClass} block underline decoration-blue-600/20 underline-offset-2`}
                                >
                                  {title}
                                  {j === 0 && (
                                    <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                                      (primary)
                                    </span>
                                  )}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {showArticlePanel && (
            <div
              ref={articleAsideRef}
              className="flex min-h-0 min-w-0 flex-col max-h-[min(70vh,calc(100vh-13rem))] lg:max-h-[calc(100vh-13rem)]"
            >
              <WikiArticlePanel
                blocks={selectedArticleBlocks}
                loading={articleViewerLoading}
                error={articleViewerError}
                onClose={closeArticleViewer}
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
